import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import HomePage from './pages/HomePage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import SubscriptionExpiredPage from './pages/SubscriptionExpiredPage';
import TrackPlayerPage from './pages/TrackPlayerPage';
import BinauralBeatsInfoPage from './pages/ResearchPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import FAQPage from './pages/FAQPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import ScrollToTop from './components/ScrollToTop';
import { cognitoAuthService } from './services/cognitoAuth';
import TracksPage from './pages/TracksPage';
import SubscriptionProtectedRoute from './components/SubscriptionProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation('common');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const auth = await cognitoAuthService.isAuthenticated();
      setIsAuthenticated(auth);
    };
    checkAuth();
  }, []);

  if (isAuthenticated === null) {
    return (
      <div
        className='min-h-screen flex items-center justify-center'
        style={{ background: 'rgb(8 6 20)' }}
      >
        <div className='text-center full-height'>
          <div className='spinner mx-auto mb-4'></div>
          <p className='text-white'>{t('loading')}</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Redirect to='/login' />;
};

const AppRoutes: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    const checkAuth = async () => {
      const auth = await cognitoAuthService.isAuthenticated();
      setIsAuthenticated(auth);
    };
    checkAuth();
  }, []);

  return (
    <Switch>
      <Route path='/auth/callback' render={() => <OAuthCallbackPage />} />
      <Route
        path='/login'
        render={() =>
          isAuthenticated ? <Redirect to='/tracks' /> : <LoginPage />
        }
      />
      <Route
        path='/register'
        render={() =>
          isAuthenticated ? <Redirect to='/tracks' /> : <RegisterPage />
        }
      />
      <Route
        path='/forgot-password'
        render={() =>
          isAuthenticated ? <Redirect to='/tracks' /> : <ForgotPasswordPage />
        }
      />
      <Route
        path='/reset-password'
        render={() =>
          isAuthenticated ? <Redirect to='/tracks' /> : <ResetPasswordPage />
        }
      />
      <Route
        path='/tracks'
        render={() => (
          <SubscriptionProtectedRoute>
            <TracksPage />
          </SubscriptionProtectedRoute>
        )}
      />
      <Route path='/binaural-info' render={() => <BinauralBeatsInfoPage />} />
      <Route path='/terms' render={() => <TermsPage />} />
      <Route path='/privacy' render={() => <PrivacyPage />} />
      <Route path='/faqs' render={() => <FAQPage />} />
      <Route path='/refund-policy' render={() => <RefundPolicyPage />} />
      <Route
        path='/subscription/success'
        render={() => (
          <ProtectedRoute>
            <SubscriptionSuccessPage />
          </ProtectedRoute>
        )}
      />
      <Route
        path='/subscription/expired'
        render={() => (
          <ProtectedRoute>
            <SubscriptionExpiredPage />
          </ProtectedRoute>
        )}
      />
      <Route
        path='/subscription'
        render={() => (
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        )}
      />
      <Route
        path='/player/:trackId'
        render={() => (
          <SubscriptionProtectedRoute>
            <TrackPlayerPage />
          </SubscriptionProtectedRoute>
        )}
      />
      <Route path='/' exact render={() => <HomePage />} />
      <Route path='*' render={() => <Redirect to='/' />} />
    </Switch>
  );
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <SubscriptionProvider>
            <ScrollToTop />
            <div className='app'>
              <AppRoutes />
            </div>
          </SubscriptionProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
};

export default App;
